/* =========================================================
   VFW Vote — the vote form's small enhancements (2026-09-17).
   The form works without this file: the car <select> posts
   contact[tags]=vote:<handle> directly. This adds:
   · "Something else" → reveals a text field; its text rides a
     second tag (vote-request:<slug>) next to vote:other.
   · Named success: before submit, remember the car; after the
     reload, name it. No count is shown: repeat votes from the same
     email are not counted and the page may be cached.
   · Preselect: the vote gallery dispatches `vfw:vote-select`
     with { handle } — select that car, bring the form into view,
     focus the email field.
   ========================================================= */

(() => {
  if (customElements.get('vfw-vote-form')) return;

  const KEY = 'vfw-vote-pending';

  const slug = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/[\s_]+/g, '-')
      .slice(0, 40);

  class VfwVoteForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.select = this.querySelector('[data-vote-select]');
      this.otherField = this.querySelector('[data-vote-other-field]');
      this.otherInput = this.querySelector('[data-vote-other-input]');
      if (!this.form || !this.select) return;

      this.select.addEventListener('change', () => this.syncOther());
      this.syncOther();

      this.form.addEventListener('submit', () => this.remember());
      document.addEventListener('vfw:vote-select', (e) => this.preselect(e.detail && e.detail.handle));

      this.showSuccess();
    }

    selectedOption() {
      return this.select.options[this.select.selectedIndex] || null;
    }

    syncOther() {
      const opt = this.selectedOption();
      const isOther = !!(opt && opt.hasAttribute('data-vote-other'));
      if (!this.otherField || !this.otherInput) return;
      this.otherField.hidden = !isOther;
      this.otherInput.required = isOther;
    }

    remember() {
      const opt = this.selectedOption();
      if (!opt || !opt.value) return;
      const isOther = opt.hasAttribute('data-vote-other');
      const typed = isOther && this.otherInput ? this.otherInput.value.trim() : '';

      if (isOther && typed) {
        // Tags are comma-separated: a hidden input carries both tags and
        // the select stops posting its own value.
        let hiddenTags = this.form.querySelector('input[data-vote-tags]');
        if (!hiddenTags) {
          hiddenTags = document.createElement('input');
          hiddenTags.type = 'hidden';
          hiddenTags.name = 'contact[tags]';
          hiddenTags.setAttribute('data-vote-tags', '');
          this.form.appendChild(hiddenTags);
        }
        hiddenTags.value = `vote:other, vote-request:${slug(typed)}`;
        this.select.removeAttribute('name');
      }

      try {
        sessionStorage.setItem(
          KEY,
          JSON.stringify({
            handle: opt.dataset.voteHandle || '',
            name: isOther ? typed : opt.dataset.voteName || '',
            total: Number(opt.dataset.voteTotal || 0),
            other: isOther,
          })
        );
      } catch (err) {
        /* storage blocked: the generic success line still shows */
      }
    }

    showSuccess() {
      const success = this.querySelector('[data-vote-success]');
      if (!success) return;

      let pending = null;
      try {
        pending = JSON.parse(sessionStorage.getItem(KEY) || 'null');
        sessionStorage.removeItem(KEY);
      } catch (err) {
        pending = null;
      }

      // Customer-form success is page-global: without our own flag this
      // reload came from a different customer form, so this form stays open.
      if (!pending) {
        success.hidden = true;
        this.revealFields();
        return;
      }

      // The line names the car but never states a count: a repeat vote from
      // the same email is (correctly) not counted, and the page can be
      // cached, so any number shown here could be wrong.
      const lead = this.querySelector('[data-vote-success-lead]');
      if (lead && pending.name) {
        lead.textContent = pending.other
          ? `Your vote for the ${pending.name} is in. We read every write-in.`
          : `Your vote for the ${pending.name} is in. Thank you.`;
      }

      const again = this.querySelector('[data-vote-again]');
      if (again) {
        again.addEventListener('click', (e) => {
          e.preventDefault();
          success.hidden = true;
          this.revealFields();
          this.select.focus();
        });
      }

      this.scrollIntoView({ block: 'center' });
      success.focus({ preventScroll: true });
    }

    revealFields() {
      const fields = this.querySelector('[data-vote-fields-after-success]');
      if (fields) fields.hidden = false;
    }

    preselect(handle) {
      if (!handle) return;
      // The pinned "Most requested" group repeats some cars; prefer the
      // make-group copy so the select shows the car under its own make.
      const matches = Array.from(this.select.options).filter((o) => o.dataset.voteHandle === handle);
      const opt = matches[matches.length - 1];
      if (!opt) return;

      const success = this.querySelector('[data-vote-success]');
      if (success && !success.hidden) {
        success.hidden = true;
        this.revealFields();
      }

      this.select.value = opt.value;
      opt.selected = true;
      this.syncOther();

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      this.classList.add('is-prefilled');

      const email = this.form.querySelector('input[type="email"]');
      if (email) email.focus({ preventScroll: true });
    }
  }

  customElements.define('vfw-vote-form', VfwVoteForm);
})();
